// Precios y fechas de disponibilidad del entorno de PRUEBA de Bókun.
//
// Módulo PURO (sin red, sin secretos): el importe a cobrar SIEMPRE se calcula
// aquí a partir de la respuesta de Bókun, nunca con datos del navegador.

type AnyRecord = Record<string, unknown>;

/**
 * Bókun devuelve `date` como epoch en milisegundos (y `localizedDate` en
 * formato humano). Normalizamos a yyyy-MM-dd.
 */
export function slotDateISO(a: AnyRecord): string | null {
  const raw = a["date"];
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  if (typeof raw === "string") {
    const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1] ?? null;
  }
  const id = a["id"];
  if (typeof id === "string") {
    const m = id.match(/_(\d{4})(\d{2})(\d{2})$/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  return null;
}

export interface CategoryPrice {
  pricingCategoryId: string;
  /** Importe por unidad en la moneda devuelta por Bókun (pesos, no centavos). */
  amount: number;
  currency: string;
  minParticipantsRequired: number | null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Precios por categoría de la tarifa indicada (o de la única disponible). */
export function categoryPricesForRate(
  pricesByRate: AnyRecord[],
  rateId?: string | number | null,
): CategoryPrice[] {
  const wanted = rateId != null ? String(rateId) : null;
  const entry =
    (wanted
      ? pricesByRate.find((p) => String(p["activityRateId"] ?? p["rateId"] ?? "") === wanted)
      : null) ?? (pricesByRate.length === 1 ? pricesByRate[0] : null);
  if (!entry) return [];
  const list = Array.isArray(entry["pricePerCategoryUnit"])
    ? (entry["pricePerCategoryUnit"] as AnyRecord[])
    : [];
  const out: CategoryPrice[] = [];
  for (const row of list) {
    const id = row["id"];
    const amount = (row["amount"] ?? {}) as AnyRecord;
    const value = num(amount["amount"]);
    if (id === undefined || id === null || value === null) continue;
    out.push({
      pricingCategoryId: String(id),
      amount: value,
      currency: typeof amount["currency"] === "string" ? (amount["currency"] as string) : "MXN",
      minParticipantsRequired: num(row["minParticipantsRequired"]),
    });
  }
  return out;
}

export interface BookingItem {
  pricingCategoryId: string;
  quantity: number;
}

export interface BookingTotalLine {
  pricingCategoryId: string;
  quantity: number;
  unitAmount: number;
  lineAmount: number;
}

export type BookingTotalResult =
  | {
      ok: true;
      amount: number;
      amountMinor: number;
      currency: string;
      lines: BookingTotalLine[];
      participants: number;
    }
  | { ok: false; code: "EMPTY" | "UNKNOWN_CATEGORY" | "BELOW_CATEGORY_MIN"; reason: string };

/** Total autoritativo: sólo suma categorías con precio devuelto por Bókun. */
export function computeBookingTotal(args: {
  prices: CategoryPrice[];
  items: BookingItem[];
}): BookingTotalResult {
  const items = args.items.filter((i) => Math.trunc(i.quantity) > 0);
  if (items.length === 0) {
    return { ok: false, code: "EMPTY", reason: "Selecciona al menos un viajero." };
  }
  const byId = new Map(args.prices.map((p) => [p.pricingCategoryId, p]));
  const lines: BookingTotalLine[] = [];
  let amount = 0;
  let participants = 0;
  let currency = args.prices[0]?.currency ?? "MXN";

  for (const item of items) {
    const price = byId.get(String(item.pricingCategoryId));
    if (!price) {
      return {
        ok: false,
        code: "UNKNOWN_CATEGORY",
        reason: "Una de las categorías de viajero ya no está disponible para esa salida.",
      };
    }
    const quantity = Math.trunc(item.quantity);
    if (price.minParticipantsRequired !== null && quantity < price.minParticipantsRequired) {
      return {
        ok: false,
        code: "BELOW_CATEGORY_MIN",
        reason: `Esta salida requiere al menos ${price.minParticipantsRequired} viajeros en una de las categorías elegidas.`,
      };
    }
    const lineAmount = Math.round(price.amount * quantity * 100) / 100;
    amount += lineAmount;
    participants += quantity;
    currency = price.currency;
    lines.push({
      pricingCategoryId: price.pricingCategoryId,
      quantity,
      unitAmount: price.amount,
      lineAmount,
    });
  }

  amount = Math.round(amount * 100) / 100;
  return { ok: true, amount, amountMinor: Math.round(amount * 100), currency, lines, participants };
}
